import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { TreatmentInfo as TreatmentInfoType } from '@customTypes/TreatmentInfo'
import { useConfig } from '@context/ConfigurationContext'
import useStyles from './TreatmentInfo.styles'
import { ReactComponent as MultipleDoctorsIcon } from 'src/images/multiple-practitioners.svg'
import { useHistory } from 'react-router-dom'
import routes from 'src/config/routes'
import { LinkButton } from '../LinkButton/LinkButton'
import clsx from 'clsx'
import { AppMode } from '@customTypes/Generic'
import Spinner from '@components/Spinner'
import { useGetTreatmentTitleWithDuration } from '@hooks/useGetTreatmentTitleWithDuration'
import { AppointmentDateAndTime } from './AppointmentDateAndTime'
import { useClinicOfficialName } from '@hooks/useClinicOfficialName'
import { TEST_IDS } from 'src/constants/testIds'
import { useGetPractitionerRoles } from '@screens/PractitionerScreen/components/PractitionerCard/helper'
import { useLanguage } from '@hooks/useLanguage'
import { getImageUrlWithTransformations } from 'src/utils/getImageUrl'
import { Resize } from '@dentalux/ui-library-core'
import { ReactComponent as PencilIcon } from 'src/images/icon-pencil.svg'
import { useIsDesktopDown } from 'src/config/mediaQueries'
import { useTheme } from '@mui/material'

type Props = {
  data: TreatmentInfoType
  variant: 'regular' | 'contactScreen'
}

const TreatmentInfo = ({ data, variant }: Props) => {
  const classes = useStyles()
  const theme = useTheme()
  const { t } = useTranslation()
  const { mode } = useConfig()
  const history = useHistory()
  const language = useLanguage()
  const isDesktopDown = useIsDesktopDown()
  const { allPractitioners, selectedPractitionerIds, practitioner, isLoading } = data
  const allPractitionersLength = allPractitioners?.length || 0
  const selectedPractitionerIdsLength = selectedPractitionerIds?.length || 0
  const allPractitionersSelected = allPractitionersLength === selectedPractitionerIdsLength
  const multiplePractitionersSelected =
    allPractitionersLength > 0 && selectedPractitionerIdsLength > 0 && !allPractitionersSelected
  const hasRecommendedPractitioner = !!practitioner
  const isNormalMode = mode === AppMode.NORMAL
  const contactScreenVariant = variant === 'contactScreen'

  const treatmentTitle = useGetTreatmentTitleWithDuration()
  const treatmentClinicOfficialName = useClinicOfficialName()
  const practitionerRole = useGetPractitionerRoles(
    practitioner?.positions,
    practitioner?.gender,
    practitioner?.displayRole,
    language
  )

  let practitionerDetailsFirstLine = ''
  let practitionerDetailsSecondLine: ReactNode = ''

  if (hasRecommendedPractitioner) {
    practitionerDetailsFirstLine = practitioner?.name || ''
    practitionerDetailsSecondLine = practitionerRole
  } else if (allPractitionersSelected) {
    practitionerDetailsFirstLine = t('selected_practitioners_all')
    practitionerDetailsSecondLine = t('showing_all_slots')
  } else if (isLoading) {
    practitionerDetailsSecondLine = <Spinner />
  } else if (multiplePractitionersSelected) {
    practitionerDetailsFirstLine = t('selected_practitioners_multiple')
  } else {
    practitionerDetailsFirstLine = t('selected_practitioners_all')
    practitionerDetailsSecondLine = t('showing_all_slots')
  }

  if (contactScreenVariant) {
    if (!hasRecommendedPractitioner) {
      practitionerDetailsFirstLine = t('one_of_selected_practitioners')
    }

    practitionerDetailsSecondLine = (
      <>
        <AppointmentDateAndTime />
        {treatmentClinicOfficialName ? (
          <>
            <br />
            <span>
              {treatmentClinicOfficialName} | {treatmentTitle}
            </span>
          </>
        ) : (
          <span> | {treatmentTitle}</span>
        )}
      </>
    )
  }

  return (
    <div
      className={clsx(classes.treatmentInfoSection, {
        [classes.treatmentInfoSectionContactScreen]: contactScreenVariant,
      })}
      data-test={TEST_IDS.dataSelectedPractitioner}
    >
      <div
        className={clsx(classes.treatmentInnerContainer, {
          [classes.treatmentInnerContainerContactScreen]: contactScreenVariant,
        })}
      >
        {contactScreenVariant && <div className={classes.appointmentSummary}>{t('appointment_summary')}</div>}
        <div className={classes.practitionerContainer}>
          <div
            className={classes.practitionerAvatar}
            style={
              hasRecommendedPractitioner && practitioner?.imageUrl
                ? {
                    backgroundImage: `url(${getImageUrlWithTransformations({
                      src: practitioner.imageUrl,
                      quality: 'lightest',
                      transformations: [new Resize(200, 200)],
                    })})`,
                  }
                : {}
            }
          >
            {!hasRecommendedPractitioner && <MultipleDoctorsIcon />}
          </div>
          <div className={classes.practitionerDetailsContainer}>
            <div
              className={clsx(classes.practitionerDetailsFirstLine, {
                [classes.removeMaxWidth]: contactScreenVariant,
              })}
            >
              {practitionerDetailsFirstLine}
            </div>
            <div className={classes.practitionerDetailsSecondLine} data-test={TEST_IDS.dataSelectedPractitionerType}>
              {practitionerDetailsSecondLine}
            </div>
          </div>
          {variant === 'regular' && isNormalMode && !isLoading && isDesktopDown && (
            <div className={classes.editPencilMobile}>
              <PencilIcon onClick={() => history.push(routes.practitioner)} fill={theme.palette.primary[800]} />
            </div>
          )}
        </div>
        {variant === 'regular' && isNormalMode && !isLoading && !isDesktopDown && (
          <LinkButton
            className={clsx(classes.selectPractitionersButton, {
              [classes.selectPractitionersButtonTopSpacing]: !!practitionerDetailsSecondLine,
            })}
            text={t('select_practitioner')}
            onClick={() => history.push(routes.practitioner)}
          />
        )}
        {isLoading && <Spinner />}
      </div>
    </div>
  )
}

export default TreatmentInfo
